import type { ComponentProps, ReactNode } from 'react';
import { Icon, Tab, TabGroup } from '@humanforce/mango-ui-react';
import { useLocation, useNavigate, useParams } from 'react-router';
import { resolveItemByPath } from '../navigation/navConfig';
import { PageTitle } from '../components/layout/PageTitle';
import { PlaceholderPanel } from './PlaceholderPage';

interface TabbedItemPageProps {
  /** Absolute item route path, e.g. "/maintenance/organisation". */
  itemPath: string;
  /** Optional hosted content for a specific sub-item slug. Parent tabs remain owned here. */
  tabContentOverrides?: Record<string, ReactNode>;
  /** Preserve the current query string when changing tabs. Defaults off for existing callers. */
  preserveSearch?: boolean;
  /** Optional Mango icons for tab headers, keyed by sub-item slug. */
  tabIcons?: Partial<Record<string, ComponentProps<typeof Icon>['name']>>;
  /** Optional page-level layout class and sibling region for full-height contextual content. */
  className?: string;
  aside?: ReactNode;
}

/**
 * Renders a navigation item that has sub-items as a Mango tab strip.
 * Tabs are URL-addressable: selecting a tab navigates to `${itemPath}/${slug}`,
 * and the active tab is derived from the `:tab` route param, so deep links work.
 */
export const TabbedItemPage = ({ itemPath, tabContentOverrides = {}, preserveSearch = false, tabIcons = {}, className, aside }: TabbedItemPageProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { '*': wildcardTab, tab: namedTab } = useParams();
  const resolved = resolveItemByPath(itemPath);
  const subItems = resolved?.item.subItems;
  if (!resolved || !subItems?.length) return null;

  const { item } = resolved;
  const tab = wildcardTab ?? namedTab;
  const activeSlug = subItems.some((s) => s.slug === tab) ? (tab as string) : subItems[0].slug;

  const content = (
    <>
      <PageTitle title={item.label} subtitle={item.note} />
      <TabGroup value={activeSlug} onValueChange={(value) => { if (value !== activeSlug) navigate(`${itemPath}/${value}${preserveSearch ? location.search : ''}`); }}>
        {subItems.map((subItem) => (
          <Tab
            key={subItem.slug}
            value={subItem.slug}
            label={subItem.label}
            leftIcon={tabIcons[subItem.slug] ? <Icon name={tabIcons[subItem.slug]!} /> : undefined}
          >
            {tabContentOverrides[subItem.slug] ?? <PlaceholderPanel name={`${item.label} — ${subItem.label}`} />}
          </Tab>
        ))}
      </TabGroup>
    </>
  );

  return className || aside ? <div className={className}><div className="tabbed-item-page__main">{content}</div>{aside}</div> : content;
};
